import * as pdfjs from "pdfjs-dist/build/pdf.mjs";

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();
}

export type PdfJsDocument = Awaited<ReturnType<typeof pdfjs.getDocument>["promise"]>;
export type PdfTextItem = { text: string; x: number; y: number; width: number; height: number };

export async function loadPdfDocument(buffer: ArrayBuffer): Promise<PdfJsDocument> {
  return pdfjs.getDocument({ data: buffer }).promise;
}

export async function renderPdfPage(
  document: PdfJsDocument,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  zoom: number,
) {
  const page = await document.getPage(pageNumber);
  const scale = zoom / 100;
  const viewport = page.getViewport({ scale });
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const canvasContext = canvas.getContext("2d");
  if (!canvasContext) throw new Error("Could not open a canvas for this page.");
  await page.render({ canvasContext, viewport }).promise;
  return { width: viewport.width / scale, height: viewport.height / scale };
}

export async function getPdfTextItems(
  document: PdfJsDocument,
  pageNumber: number,
  zoom: number,
): Promise<PdfTextItem[]> {
  const page = await document.getPage(pageNumber);
  const scale = zoom / 100;
  const viewport = page.getViewport({ scale });
  const content = await page.getTextContent();
  return content.items
    .filter((item: { str?: string }) => typeof item.str === "string" && item.str.trim())
    .map((item: { str: string; transform: number[]; width: number }) => {
      const transform = pdfjs.Util.transform(viewport.transform, item.transform);
      const height = Math.max(10, Math.hypot(transform[2], transform[3]));
      return {
        text: item.str,
        x: transform[4],
        y: transform[5] - height,
        width: Math.max(8, item.width * scale),
        height,
      };
    });
}

export async function buildPdfThumbnails(
  pdfDocument: PdfJsDocument,
  scale = 0.18,
  onThumbnail?: (dataUrl: string) => void,
  signal?: AbortSignal,
) {
  const thumbnails: string[] = [];
  for (let index = 1; index <= pdfDocument.numPages; index += 1) {
    if (signal?.aborted) throw new DOMException("Thumbnail generation was cancelled.", "AbortError");
    const page = await pdfDocument.getPage(index);
    const viewport = page.getViewport({ scale });
    const canvas = globalThis.document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const canvasContext = canvas.getContext("2d");
    if (!canvasContext) continue;
    await page.render({ canvasContext, viewport }).promise;
    const dataUrl = canvas.toDataURL("image/jpeg", 0.65);
    thumbnails.push(dataUrl);
    onThumbnail?.(dataUrl);
  }
  return thumbnails;
}
